<!-- ProFeatureChip.vue - Complete modern component -->
<template>
  <div
    class="pro-feature-chip"
    :class="[
      chipClass,
      {
        'is-active': isActive,
        'has-shimmer': hasShimmer,
        'has-pulse': hasPulse
      }
    ]"
    @mouseenter="showTooltip = true"
    @mouseleave="showTooltip = false"
  >
    <!-- Icon -->
    <div v-if="showIcon" class="chip-icon">
      <svg v-if="chipIcon === 'crown'" viewBox="0 0 20 20" fill="currentColor">
        <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15v2a1 1 0 01-1 1H6a1 1 0 01-1-1v-2a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1zm-5 8.274l-.818 2.552c.25.112.526.174.818.174 2.21 0 4-.672 4-1.5s-1.79-1.5-4-1.5zm10 0c-2.21 0-4 .672-4 1.5s1.79 1.5 4 1.5c.292 0 .568-.062.818-.174l-.818-2.552z"/>
      </svg>
      <svg v-else-if="chipIcon === 'users'" viewBox="0 0 20 20" fill="currentColor">
        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
      </svg>
      <svg v-else-if="chipIcon === 'star'" viewBox="0 0 20 20" fill="currentColor">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
      </svg>
    </div>

    <!-- Label -->
    <span class="chip-label">{{ chipLabel }}</span>

    <!-- Shimmer effect -->
    <div v-if="hasShimmer && isActive" class="shimmer-effect"></div>

    <!-- Tooltip -->
    <transition name="tooltip-fade">
      <div v-if="showTooltip && tooltip" class="chip-tooltip">
        <div class="tooltip-arrow"></div>
        {{ tooltip }}
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'ProFeatureChip',
  props: {
    isPro: {
      type: Boolean,
      default: false
    },
    isTeam: {
      type: Boolean,
      default: false
    },
    isActive: {
      type: Boolean,
      default: false
    },
    isTeamPro: {
      type: Boolean,
      default: false
    },
    // New props for enhanced functionality
    showIcon: {
      type: Boolean,
      default: true
    },
    hasShimmer: {
      type: Boolean,
      default: true
    },
    hasPulse: {
      type: Boolean,
      default: false
    },
    customTooltip: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showTooltip: false
    }
  },
  computed: {
    chipClass() {
      if (this.isTeamPro || this.isTeam) {
        return 'chip-team';
      }
      return 'chip-pro';
    },
    chipIcon() {
      if (this.isTeamPro || this.isTeam) {
        return 'users';
      }
      return this.isPro ? 'crown' : 'star';
    },
    chipLabel() {
      if (this.isTeamPro || this.isTeam) {
        return 'TEAM PRO';
      }
      return 'PRO';
    },
    tooltip() {
      if (this.customTooltip) {
        return this.customTooltip;
      }
      if (this.isTeamPro || this.isTeam) {
        return 'Team Pro: Premium features for your entire team';
      }
      return 'Pro: Unlock advanced features and priority support';
    }
  }
}
</script>

<style scoped>
.pro-feature-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  user-select: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: default;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid transparent;
}

/* Pro variant */
.chip-pro {
  background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
  color: #6b7280;
  border-color: #e5e7eb;
}

.chip-pro.is-active {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

/* Team variant */
.chip-team {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #92400e;
  border-color: #fde68a;
}

.chip-team.is-active {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

/* Icon styling */
.chip-icon {
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chip-icon svg {
  width: 100%;
  height: 100%;
}

/* Label */
.chip-label {
  line-height: 1;
}

/* Shimmer effect */
.shimmer-effect {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shimmer 3s infinite;
  pointer-events: none;
}

/* Tooltip */
.chip-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
}

.tooltip-arrow {
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid rgba(0, 0, 0, 0.9);
}

/* Hover effects */
.pro-feature-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.chip-pro.is-active:hover {
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
}

.chip-team.is-active:hover {
  box-shadow: 0 6px 16px rgba(245, 158, 11, 0.4);
}

/* Pulse animation */
.has-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Animations */
@keyframes shimmer {
  to {
    left: 100%;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

/* Transitions */
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.2s ease;
}

.tooltip-fade-enter,
.tooltip-fade-leave-to {
  opacity: 0;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .pro-feature-chip {
    padding: 4px 10px;
    font-size: 10px;
    gap: 4px;
  }

  .chip-icon {
    width: 12px;
    height: 12px;
  }
}
</style>
